import { Outlet, Link } from "react-router-dom";
import { Navbar } from "./Navbar";
import { CookieConsent } from "./CookieConsent";
import { AuthModal } from "./AuthModal";
import { useTheme } from "@/src/lib/useTheme";

export function Layout() {
  const { theme } = useTheme();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  return (
    <div className={`min-h-screen flex flex-col font-sans selection:bg-emerald-500/30 transition-colors duration-300 ${theme === 'dark' ? 'bg-[#050505] text-neutral-50' : 'bg-[#f8fafb] text-neutral-900'}`}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-emerald-600 focus:text-white focus:rounded">Skip to content</a>
      <Navbar />
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-950">
        <nav aria-label="Footer navigation">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" onClick={scrollToTop} className="text-xl font-light tracking-wide">
                p<span className="text-emerald-500">α</span>rb<span className="text-emerald-500">ε</span>rkon
              </Link>
              <p className="text-neutral-400 dark:text-white/40 text-xs mt-1 uppercase tracking-[0.2em]">
                cyprus barbershop directory
              </p>
              <p className="text-neutral-500 dark:text-white/50 text-sm mt-3 leading-relaxed">
                Cyprus' #1 barbershop directory. Find the best barbers near you across all major cities.
              </p>
            </div>
            {/* Cities */}
            <div>
              <h4 className="font-semibold text-neutral-700 dark:text-white/80 mb-3 text-sm uppercase tracking-wider">Top Cities</h4>
              <ul className="space-y-2 text-sm text-neutral-500 dark:text-white/50">
                <li><Link to="/cities/limassol" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Limassol</Link></li>
                <li><Link to="/cities/nicosia" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Nicosia</Link></li>
                <li><Link to="/cities/paphos" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Paphos</Link></li>
                <li><Link to="/cities/larnaca" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Larnaca</Link></li>
                <li><Link to="/cities/famagusta" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Famagusta</Link></li>
              </ul>
            </div>
            {/* Directory */}
            <div>
              <h4 className="font-semibold text-neutral-700 dark:text-white/80 mb-3 text-sm uppercase tracking-wider">Directory</h4>
              <ul className="space-y-2 text-sm text-neutral-500 dark:text-white/50">
                <li><Link to="/" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">All Shops</Link></li>
                <li><Link to="/leaderboard" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Leaderboard</Link></li>
                <li><Link to="/profile" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">My Profile</Link></li>
              </ul>
            </div>
            {/* Info & Legal */}
            <div>
              <h4 className="font-semibold text-neutral-700 dark:text-white/80 mb-3 text-sm uppercase tracking-wider">Legal &amp; Company</h4>
              <ul className="space-y-2 text-sm text-neutral-500 dark:text-white/50">
                <li><Link to="/terms-of-service" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy-policy" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/cookie-policy" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Cookie Policy</Link></li>
                <li><Link to="/disclaimer" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Listing Disclaimer &amp; Liability</Link></li>
                <li><Link to="/company-info" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Company Info &amp; Disclosures</Link></li>
                <li><Link to="/contact" onClick={scrollToTop} className="hover:text-emerald-400 transition-colors">Contact Us</Link></li>
              </ul>
            </div>
          </div>

          {/* All Cyprus Municipalities & Villages Internal Link Silo (SEO Crawl Optimization) */}
          <div className="border-t border-neutral-200 dark:border-white/10 pt-6 pb-6 mb-6">
            <h5 className="text-xs font-bold text-neutral-700 dark:text-white/80 uppercase tracking-wider mb-3">
              Browse Cyprus Barbershops by City &amp; Municipality
            </h5>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-neutral-500 dark:text-white/50">
              {[
                { name: "Limassol", slug: "limassol" },
                { name: "Nicosia", slug: "nicosia" },
                { name: "Larnaca", slug: "larnaca" },
                { name: "Paphos", slug: "paphos" },
                { name: "Famagusta", slug: "famagusta" },
                { name: "Kyrenia", slug: "kyrenia" },
                { name: "Paralimni", slug: "paralimni" },
                { name: "Strovolos", slug: "strovolos" },
                { name: "Ayia Napa", slug: "ayia-napa" },
                { name: "Lakatamia", slug: "lakatamia" },
                { name: "Protaras", slug: "protaras" },
                { name: "Avgorou", slug: "avgorou" },
                { name: "Sotira", slug: "sotira" },
                { name: "Frenaros", slug: "frenaros" },
                { name: "Pissouri", slug: "pissouri" },
                { name: "Xylofagou", slug: "xylofagou" },
                { name: "Athienou", slug: "athienou" },
                { name: "Peyia", slug: "peyia" },
                { name: "Poli Crysochous", slug: "poli-crysochous" },
                { name: "Karavas", slug: "karavas" },
                { name: "Lapta", slug: "lapta" },
                { name: "Kyperounta", slug: "kyperounta" },
                { name: "Pergamos", slug: "pergamos" },
                { name: "Pyla", slug: "pyla" },
                { name: "Episkopi", slug: "episkopi" },
                { name: "Kissonerga", slug: "kissonerga" },
                { name: "Foini", slug: "foini" },
                { name: "Nisou", slug: "nisou" },
                { name: "Agia Varvara", slug: "agia-varvara" },
                { name: "Pano Lefkara", slug: "pano-lefkara" },
                { name: "Mosfiloti", slug: "mosfiloti" },
                { name: "Deftera", slug: "deftera" },
                { name: "Kofinou", slug: "kofinou" },
                { name: "Perivolia", slug: "perivolia" },
                { name: "Peristerona", slug: "peristerona" },
                { name: "Klirou", slug: "klirou" },
                { name: "Astromeritis", slug: "astromeritis" },
                { name: "Akaki", slug: "akaki" },
                { name: "Agrokipia", slug: "agrokipia" },
                { name: "Arediou", slug: "arediou" },
                { name: "Kokkinotrimithia", slug: "kokkinotrimithia" },
                { name: "Palaiometocho", slug: "palaiometocho" },
                { name: "Pareklisia", slug: "pareklisia" },
              ].map((c, i, arr) => (
                <span key={c.slug} className="inline-flex items-center gap-2">
                  <Link
                    to={`/cities/${c.slug}`}
                    onClick={scrollToTop}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    {c.name}
                  </Link>
                  {i < arr.length - 1 && <span className="text-neutral-300 dark:text-white/20 select-none">•</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-neutral-200 dark:border-white/10 pt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-neutral-400 dark:text-white/40 text-sm">&copy; {new Date().getFullYear()} pαrbεrkon (Zebeh). All rights reserved.</p>
            <div className="flex items-center gap-2">
              <span className="text-amber-400/90 text-sm">Operated by</span>
              <a
                href="https://zebeh.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-pink-500 via-rose-500 to-amber-400 hover:opacity-90 transition-opacity"
              >
                zebeh.com
              </a>
            </div>
          </div>
        </div>
        </nav>
      </footer>
      <CookieConsent />
      <AuthModal />
    </div>
  );
}
